import React, { useRef, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Collapsible } from '@opendoor/atoms/collapsible';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import * as Sentry from '@sentry/nextjs';
import { IconChevronDown } from '@tabler/icons-react';
import { getSellerDisclosureDownload } from 'components/marketplace/helpers/sellerDisclosures';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import LongTextNew from 'components/properties/shared/LongTextNew';
import { Divider } from 'components/shared/Divider';
import { formatCurrency } from 'helpers/formatters';

import { convertDwellingType, formatLotSize } from './helpers';

import styles from './HomeDetails.module.css';

type HomeDetailsProperty = Pick<
  MarketplaceProperty,
  | 'addressToken'
  | 'daysOnMarket'
  | 'description'
  | 'dwellingType'
  | 'exteriorStories'
  | 'garageSpaces'
  | 'hasSellerDisclosures'
  | 'hoaMonthlyDues'
  | 'kitchenCountertop'
  | 'listingState'
  | 'primaryBedroomLocation'
  | 'pricePerSqFt'
  | 'sqFtLot'
  | 'yearBuilt'
>;

interface HomeDetailsProps {
  property: HomeDetailsProperty;
  layout?: 'details' | 'overview';
}

const kitchenCountertopLabel = {
  CORIAN: 'Corian',
  FORMICA: 'Formica',
  GRANITE_SLAB: 'Granite slab',
  GRANITE_TILE: 'Granite tile',
  OTHER_TILE: 'Other',
  QUARTZ: 'Quartz',
  UNKNOWN: 'Other',
} satisfies Record<NonNullable<HomeDetailsProperty['kitchenCountertop']>, string>;

const convertPrimaryBedroomLocation = (
  primaryBedroomLocation: HomeDetailsProperty['primaryBedroomLocation'],
  exteriorStories: HomeDetailsProperty['exteriorStories'],
) => {
  const labels = {
    BASEMENT: null,
    FIRST_FLOOR: exteriorStories && exteriorStories > 1 ? 'Downstairs bedroom' : 'Main bedroom',
    SECOND_FLOOR: 'Upstairs bedroom',
    THIRD_FLOOR: 'Upstairs bedroom',
    UNKNOWN: null,
  } satisfies Record<NonNullable<HomeDetailsProperty['primaryBedroomLocation']>, string | null>;
  return primaryBedroomLocation === null ? null : (labels[primaryBedroomLocation] ?? null);
};

const listedAge = (daysOnMarket: HomeDetailsProperty['daysOnMarket']) => {
  if (daysOnMarket == null) return null;
  if (daysOnMarket === 0) return 'Today';
  return `${daysOnMarket} ${daysOnMarket === 1 ? 'day' : 'days'} ago`;
};

export const HomeDetails: React.FC<HomeDetailsProps> = ({
  property,
  layout = 'details',
}: HomeDetailsProps) => {
  const [isDownloadingDisclosure, setIsDownloadingDisclosure] = useState(false);
  const [disclosureError, setDisclosureError] = useState<string | null>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const descriptionViewportRef = useRef<HTMLElement>(null);
  const downloadSellerDisclosure = async () => {
    setIsDownloadingDisclosure(true);
    setDisclosureError(null);
    try {
      const result = await getSellerDisclosureDownload(property.addressToken);
      if (result.status === 'unavailable') {
        setDisclosureError('This seller’s disclosure is no longer available.');
      } else {
        window.location.assign(result.url);
      }
    } catch (error) {
      // GraphQL transport errors may include response data, so report only bounded metadata.
      Sentry.captureException(new Error('Seller disclosure download failed'), {
        tags: {
          feature: 'marketplace-property',
          operation: 'GetMarketplacePropertySellerDisclosures',
        },
        extra: {
          addressId: property.addressToken,
          upstreamErrorName: error instanceof Error ? error.name : typeof error,
        },
      });
      setDisclosureError('We couldn’t download the seller’s disclosure. Please try again.');
    } finally {
      setIsDownloadingDisclosure(false);
    }
  };
  const primaryBedroomLocation = convertPrimaryBedroomLocation(
    property.primaryBedroomLocation,
    property.exteriorStories,
  );
  const listedRow = { title: 'Listed', value: listedAge(property.daysOnMarket) };
  const lotSize = formatLotSize(property.sqFtLot);

  const rows: Array<{ title: string; value: string | number | null }> = [
    {
      title: 'Home type',
      value: convertDwellingType(property.dwellingType),
    },
    {
      title: 'Lot size',
      value: lotSize ? `${lotSize}${layout === 'details' ? ' lot' : ''}` : null,
    },
    ...(layout === 'details'
      ? [
          {
            title: 'Price per sqft',
            value: property.pricePerSqFt
              ? `${formatCurrency(property.pricePerSqFt, { inDollars: true })} / sqft`
              : null,
          },
        ]
      : []),
    {
      title: 'Built in',
      value: property.yearBuilt,
    },
    {
      title: 'Kitchen',
      value: property.kitchenCountertop ? kitchenCountertopLabel[property.kitchenCountertop] : null,
    },
    {
      title: 'Garage',
      value: property.garageSpaces ? `${property.garageSpaces} car garage` : null,
    },
    {
      title: 'Master bedroom',
      value: primaryBedroomLocation,
    },
  ];

  const overviewRows = rows.slice(0, 3);
  overviewRows.push({
    title: 'HOA',
    value:
      property.hoaMonthlyDues == null
        ? 'None'
        : `${formatCurrency(property.hoaMonthlyDues, { inDollars: true })} / mo`,
  });
  if (property.listingState === 'ON_THE_MARKET') overviewRows.push(listedRow);
  const filteredRows = (layout === 'overview' ? overviewRows : rows).filter(
    (row) => row.value !== null,
  );
  const showPropertyDescription = property.listingState === 'ON_THE_MARKET';
  const canCollapseDescription = (property.description?.length ?? 0) > 240;
  const toggleDescription = () => {
    if (!isDescriptionExpanded) {
      const viewport = descriptionViewportRef.current;
      const description = viewport?.querySelector('#pdp-about-description');
      if (viewport && description instanceof HTMLElement) {
        viewport.style.setProperty(
          '--description-expanded-height',
          `${description.scrollHeight}px`,
        );
      }
    }
    setIsDescriptionExpanded((expanded) => !expanded);
  };

  const disclosure = property.hasSellerDisclosures !== false && (
    <Col className="py-6">
      <Button
        analyticsName="seller_disclosure_link"
        onClick={() => void downloadSellerDisclosure()}
        loading={isDownloadingDisclosure}
        size="lg"
        variant="secondary"
        aria-label="Download seller’s disclosure"
        className="w-full"
      >
        Download seller&rsquo;s disclosure
      </Button>
      {disclosureError && (
        <Text className="pt-3 text-body-sm text-od-content-error" role="alert">
          {disclosureError}
        </Text>
      )}
    </Col>
  );

  if (layout === 'overview') {
    return (
      <Col className="gap-10">
        <Col as="section" aria-label="Details" className="gap-6">
          <Text as="h2" className="text-label-lg">
            Details
          </Text>
          <Box
            role="group"
            aria-label="Home facts"
            className="grid grid-cols-2 gap-x-6 gap-y-6 lg:grid-cols-4"
          >
            {filteredRows.map((row) => (
              <Col key={row.title} className="gap-1">
                <Text as="span" className="text-body-sm text-od-content-secondary">
                  {row.title}
                </Text>
                <Text as="span" className="text-body-md">
                  {row.value}
                </Text>
              </Col>
            ))}
          </Box>
        </Col>
        <Col as="section" aria-label="About this home" className="gap-6">
          <Text as="h2" className="text-label-lg">
            About this home
          </Text>
          {showPropertyDescription && property.description && (
            <Col className="w-full items-center gap-2 rounded-xl bg-transparent">
              <Box
                ref={descriptionViewportRef}
                data-expanded={isDescriptionExpanded}
                className={cn(
                  'relative w-full',
                  canCollapseDescription && styles.descriptionViewport,
                  canCollapseDescription &&
                    isDescriptionExpanded &&
                    styles.descriptionViewportExpanded,
                )}
              >
                <Text
                  id="pdp-about-description"
                  as="p"
                  className="whitespace-pre-line text-pretty text-body-md text-od-content-secondary"
                >
                  {property.description}
                </Text>
                {canCollapseDescription && (
                  <Box
                    aria-hidden="true"
                    data-expanded={isDescriptionExpanded}
                    className={cn(
                      'description-fade pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-b from-transparent via-od-bg-primary/75 to-od-bg-primary backdrop-blur-[1px] [mask-image:linear-gradient(to_bottom,transparent,black)]',
                      styles.descriptionFade,
                      isDescriptionExpanded && styles.descriptionFadeHidden,
                    )}
                  />
                )}
              </Box>
              {canCollapseDescription && (
                <Button
                  variant="secondary"
                  size="sm"
                  analyticsName="pdp-refresh-description-toggle"
                  aria-label={isDescriptionExpanded ? 'See less' : 'See more'}
                  aria-expanded={isDescriptionExpanded}
                  aria-controls="pdp-about-description"
                  onClick={toggleDescription}
                  className="h-8 self-center border-0 bg-transparent px-2 !text-od-content-secondary shadow-none transition-colors duration-150 ease-out hover:bg-od-bg-hover-primary"
                >
                  {isDescriptionExpanded ? 'See less' : 'See more'}
                  <IconChevronDown
                    size={16}
                    aria-hidden="true"
                    className={`transition-transform duration-150 ease-out ${
                      isDescriptionExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </Button>
              )}
            </Col>
          )}
        </Col>
      </Col>
    );
  }

  return (
    <Collapsible.Root defaultOpen className="w-full">
      <Collapsible.Trigger className="group flex w-full items-center justify-between gap-2 py-6">
        <Text
          as="span"
          className="text-left text-label-xl text-od-content-primary lg:text-subheader-xs"
        >
          Home details
        </Text>
        <Box className="shrink-0 text-od-content-primary transition-transform duration-200 group-data-[panel-open]:rotate-180">
          <IconChevronDown size={20} />
        </Box>
      </Collapsible.Trigger>
      <Collapsible.Panel className="pb-4">
        <Col className="w-full" id="homeDetails">
          {showPropertyDescription && property.description && (
            <Col className="pb-6 max-md:pb-11">
              <LongTextNew content={property.description} limit={212} />
            </Col>
          )}
          {filteredRows.map((row, index) => (
            <Col key={row.title}>
              <Row className="w-full justify-between">
                <Text className="text-body-sm text-od-content-primary">{row.title}</Text>
                <Text className="text-label-sm text-od-content-primary">{row.value}</Text>
              </Row>
              {index !== filteredRows.length - 1 ? <Divider className="py-4" /> : null}
            </Col>
          ))}
          {disclosure}
        </Col>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
};
